/**
 * 개요 페이지 — 맵 에디터로 만든 맵을 읽기전용(pan/zoom만)으로 보여주는 위젯.
 *
 * 현장(SiteProject)에는 "개요에 표시할 맵" 하나만 지정할 수 있고(dashboard_map_project_id),
 * 어떤 맵을 보여줄지 선택/변경하는 것만 가능하다 — 위젯 자체를 지우는 기능은 없다.
 *
 * 권한: 조회는 누구나, 맵 선택/변경은 canEdit(admin/editor).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Map as MapIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import usePermissions from '../../../hooks/usePermissions';
import { SiteProjectService } from '../../../services/SiteProjectService';
import { mapEditorService } from '../../../map-editor-shared/api/services/mapEditorService';
import type { MapProject } from '../../../map-editor-shared/api/types';
import { useProjectInitializer } from '../../../map-editor-shared/hooks/useProjectInitializer';
import { useFloorStore } from '../../../map-editor-shared/store/floorStore';
import { CanvasFloorViewer } from '../../../map-editor-features/viewer';
import { ThemeProvider as MapEditorThemeProvider } from '../../../map-editor-shared/context/ThemeContext';

interface Props {
  projectId: number;
}

const MapOverviewWidget: React.FC<Props> = ({ projectId }) => {
  const { canEdit } = usePermissions();
  const navigate = useNavigate();

  // undefined = 조회 중, null = 미설정
  const [mapProjectId, setMapProjectId] = useState<number | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [availableMaps, setAvailableMaps] = useState<MapProject[] | null>(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadSiteProject = useCallback(() => {
    setLoadError(null);
    SiteProjectService.getProject(projectId)
      .then((p) => setMapProjectId(p.dashboard_map_project_id ?? null))
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : '개요 설정 조회 실패');
        setMapProjectId(null);
      });
  }, [projectId]);

  useEffect(() => {
    loadSiteProject();
  }, [loadSiteProject]);

  const openPicker = () => {
    setPickerOpen(true);
    setPickerError(null);
    setPickerLoading(true);
    mapEditorService
      .getProjects(projectId)
      .then((res) => setAvailableMaps(res.success ? res.data ?? [] : []))
      .catch((e) => setPickerError(e instanceof Error ? e.message : '맵 목록 조회 실패'))
      .finally(() => setPickerLoading(false));
  };

  const handleSelect = async (id: number) => {
    setSaving(true);
    setPickerError(null);
    try {
      await SiteProjectService.updateProject(projectId, { dashboard_map_project_id: id });
      setMapProjectId(id);
      setPickerOpen(false);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : '맵 설정 저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const mapIdStr = mapProjectId != null ? String(mapProjectId) : undefined;
  const {
    loading: mapLoading,
    error: mapError,
    project: mapProject,
  } = useProjectInitializer(mapIdStr, mapIdStr);
  const floors = useFloorStore((state) => (mapIdStr ? state.getFloorsByLotId(mapIdStr) : []));
  // 지상 1층(order === 0)을 기본으로, 없으면 가장 낮은 순번의 층을 보여준다.
  const displayFloor = floors.find((f) => f.order === 0) ?? floors[0];

  const goToFullView = () => {
    if (mapIdStr) navigate(`/project/${projectId}/map-viewer/${mapIdStr}`);
  };

  const goToMapEditor = () => {
    navigate(`/project/${projectId}/map-editor/guided`);
  };

  let body: React.ReactNode;

  if (mapProjectId === undefined) {
    body = (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 120 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  } else if (mapProjectId === null) {
    body = (
      <Stack spacing={1} alignItems="flex-start" sx={{ color: 'text.secondary' }}>
        <Typography variant="body2">표시할 맵이 설정되지 않았습니다.</Typography>
        {canEdit && (
          <Button size="small" variant="outlined" onClick={openPicker}>
            맵 선택
          </Button>
        )}
      </Stack>
    );
  } else if (mapLoading) {
    body = (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 120 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  } else if (mapError || !mapProject) {
    body = (
      <Typography variant="body2" color="warning.main">
        맵을 불러올 수 없습니다{mapError ? `: ${mapError}` : ''}.
      </Typography>
    );
  } else if (!displayFloor) {
    body = (
      <Typography variant="body2" color="text.secondary">
        맵에 표시할 층 데이터가 없습니다.
      </Typography>
    );
  } else {
    body = (
      // CanvasFloorViewer — 시뮬레이터와 동일한 Canvas 렌더러(휠 줌·드래그 팬 내장,
      // ResizeObserver 로 위젯 카드 리사이즈에도 맞춰 다시 그림). minHeight 는 좁은
      // 화면(세로 스택, 부모에 확정 높이가 없어 flex:1 이 효과가 없는 상황)에서도
      // 지도가 완전히 찌그러지지 않게 하는 바닥값이다.
      <Box sx={{ flex: 1, minHeight: 120, borderRadius: 1, overflow: 'hidden' }}>
        <MapEditorThemeProvider>
          <CanvasFloorViewer floor={displayFloor} />
        </MapEditorThemeProvider>
      </Box>
    );
  }

  return (
    <>
      <WidgetCard
        title="맵"
        icon={<MapIcon fontSize="small" />}
        actionLabel={mapProjectId != null ? '전체화면으로 보기' : undefined}
        onAction={mapProjectId != null ? goToFullView : undefined}
      >
        <Stack spacing={1} sx={{ height: '100%' }}>
          {loadError && (
            <Typography variant="caption" color="warning.main">
              {loadError}
            </Typography>
          )}
          {body}
          {canEdit && mapProjectId != null && (
            <Box sx={{ textAlign: 'right' }}>
              <Button size="small" onClick={openPicker}>
                맵 변경
              </Button>
            </Box>
          )}
        </Stack>
      </WidgetCard>

      <Dialog open={pickerOpen} onClose={() => !saving && setPickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>개요에 표시할 맵 선택</DialogTitle>
        <DialogContent dividers>
          {pickerLoading && (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={20} />
            </Stack>
          )}
          {pickerError && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
              {pickerError}
            </Typography>
          )}
          {!pickerLoading && availableMaps && availableMaps.length === 0 && (
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                아직 이 현장에 생성된 맵이 없습니다.
              </Typography>
              <Button size="small" variant="contained" onClick={goToMapEditor}>
                맵 에디터에서 만들기
              </Button>
            </Stack>
          )}
          {!pickerLoading && availableMaps && availableMaps.length > 0 && (
            <List disablePadding>
              {availableMaps.map((m) => (
                <ListItemButton
                  key={m.id}
                  selected={m.id === mapProjectId}
                  disabled={saving}
                  onClick={() => handleSelect(m.id)}
                >
                  <ListItemText primary={m.name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={saving}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapOverviewWidget;
